import {
  CacheLookupPolicy,
  InteractionRequiredAuthError,
  type AccountInfo,
  type IPublicClientApplication
} from '@azure/msal-browser';

import { classifyAuthError } from '@/lib/auth-errors';
import type { SilentTokenOptions } from '@/lib/auth-config';
import { AZURE_DEVOPS_RESOURCE_SCOPE } from '@/lib/azure-devops/types';

export const AZURE_DEVOPS_SCOPES: readonly string[] = [AZURE_DEVOPS_RESOURCE_SCOPE];

const LEGACY_SILENT_OPTIONS: Omit<SilentTokenOptions, 'redirectUri'> = {
  cacheLookupPolicy: CacheLookupPolicy.AccessTokenAndRefreshToken
};

export type AzureDevOpsTokenErrorCode =
  | 'consent_required'
  | 'interaction_started'
  | 'interaction_blocked'
  | 'authentication_expired'
  | 'token_unavailable';

const TOKEN_ERROR_MESSAGES: Record<AzureDevOpsTokenErrorCode, string> = {
  consent_required:
    'Azure DevOps access needs Microsoft consent. Approve access, then try again.',
  interaction_started:
    'Complete Azure DevOps consent in the browser tab that just opened, then try again.',
  interaction_blocked:
    'Your browser blocked the Azure DevOps consent tab. Allow popups for this site and try again.',
  authentication_expired:
    'Your Microsoft session has expired. Sign in again before accessing Azure DevOps.',
  token_unavailable:
    'An Azure DevOps access token could not be acquired. Try again.'
};

export class AzureDevOpsTokenError extends Error {
  readonly code: AzureDevOpsTokenErrorCode;

  constructor(code: AzureDevOpsTokenErrorCode, cause?: unknown) {
    super(TOKEN_ERROR_MESSAGES[code], cause === undefined ? undefined : { cause });
    this.name = 'AzureDevOpsTokenError';
    this.code = code;
  }
}

export interface AcquireAzureDevOpsTokenOptions {
  instance: IPublicClientApplication;
  account: AccountInfo;
  /** The provider's silent request options; defaults to the legacy refresh-token policy. */
  silentRequest?: SilentTokenOptions | Omit<SilentTokenOptions, 'redirectUri'>;
  /**
   * Starts user-visible consent. Returns an access token when consent finished
   * in place (popup), true when it continues elsewhere, false when blocked.
   */
  requestInteractiveConsent: () => Promise<boolean | string>;
}

export async function acquireAzureDevOpsAccessToken({
  instance,
  account,
  silentRequest = LEGACY_SILENT_OPTIONS,
  requestInteractiveConsent
}: AcquireAzureDevOpsTokenOptions): Promise<string> {
  try {
    const result = await instance.acquireTokenSilent({
      ...silentRequest,
      scopes: [...AZURE_DEVOPS_SCOPES],
      account
    });
    if (!result.accessToken) throw new AzureDevOpsTokenError('token_unavailable');
    return result.accessToken;
  } catch (error) {
    if (error instanceof AzureDevOpsTokenError) throw error;
    const failure = classifyAuthError(error);
    if (error instanceof InteractionRequiredAuthError || failure === 'interactionRequired') {
      let started: boolean | string = false;
      try {
        started = await requestInteractiveConsent();
      } catch (interactionError) {
        throw new AzureDevOpsTokenError('consent_required', interactionError);
      }
      if (typeof started === 'string' && started) return started;
      throw new AzureDevOpsTokenError(
        started ? 'interaction_started' : 'interaction_blocked',
        error
      );
    }
    if (failure === 'cancelled') {
      throw new AzureDevOpsTokenError('consent_required', error);
    }
    throw new AzureDevOpsTokenError('token_unavailable', error);
  }
}
